import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Upload, Trash2, Calendar, Clock, MapPin, Phone, Package, ClipboardList, Users, AlertCircle, UserCog, HelpCircle, X, ChevronRight, Mail, Send, User, Download, Sparkles, Map as MapIcon, ArrowRight, Loader2, Paperclip, FileText } from "lucide-react";

const DAY_KEYS = ["day_domenica", "day_lunedi", "day_martedi", "day_mercoledi", "day_giovedi", "day_venerdi", "day_sabato"];

const UNMATCHED = "__unmatched__";

export default function AssegnazioniImpianti() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const formatDay = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso + "T00:00:00");
      return `${t(`auto.${DAY_KEYS[d.getDay()]}`)} ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
    } catch { return iso; }
  };
  const [technicians, setTechnicians] = useState([]);
  const [tech, setTech] = useState("");
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  // Selezione per invio email (Set di assignment.id). All'load, pre-seleziona
  // automaticamente solo gli appuntamenti "nuovi" (senza email_sent_at).
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [openUpload, setOpenUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // Column mapping preview
  const [previewData, setPreviewData] = useState(null); // { columns, default_mapping, field_labels, sheet_name }
  const [previewing, setPreviewing] = useState(false);
  const [colMapping, setColMapping] = useState({}); // { field: columnIndex | null }

  const [openAliases, setOpenAliases] = useState(false);
  const [aliasDrafts, setAliasDrafts] = useState({}); // {techId: "csv text"}

  // Route planning
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [openOptimize, setOpenOptimize] = useState(false);
  const [applyingRoute, setApplyingRoute] = useState(false);

  // Invio email agenda giornaliera
  const [openEmail, setOpenEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: [], // array di destinatari
    toInput: "", // input corrente per aggiungere
    reply_to: "",
    from_name: "",
    extra_message: "",
    smtp_account_id: "",
    attachments: [], // {filename, mime_type, size, content_base64}
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [me, setMe] = useState(null);
  const [settings, setSettings] = useState(null);
  const [smtpAccounts, setSmtpAccounts] = useState([]);

  const loadTechs = async () => {
    const { data } = await api.get("/users", { params: { role: "tecnico,subappalto" } });
    setTechnicians(data);
    if (data.length && !tech) setTech(data[0].id);
    // preload alias drafts
    const drafts = {};
    data.forEach((tech) => { drafts[tech.id] = (tech.aliases || []).join(", "); });
    setAliasDrafts(drafts);
  };

  const loadUnmatchedCount = async () => {
    try {
      const { data } = await api.get("/assignments/unmatched-count");
      setUnmatchedCount(data.count || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadTechs(); loadUnmatchedCount(); }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/smtp-accounts").then((r) => setSmtpAccounts(r.data)).catch(() => {});
  }, []);

  const loadDates = async (t) => {
    if (!t) return;
    const { data } = await api.get("/assignments/dates", { params: { technician_id: t } });
    setDates(data);
    if (data.length) {
      const today = new Date().toISOString().slice(0, 10);
      const upcoming = data.find((d) => d.date >= today) || data[data.length - 1];
      setSelectedDate(upcoming.date);
    } else {
      setSelectedDate("");
      setAssignments([]);
    }
  };

  const loadAssignments = async () => {
    if (!tech || !selectedDate) { setAssignments([]); setSelectedIds(new Set()); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/assignments", { params: { technician_id: tech, date: selectedDate } });
      setAssignments(data);
      // Auto-preseleziona solo gli appuntamenti "nuovi" (mai inviati)
      setSelectedIds(new Set(data.filter((a) => !a.email_sent_at).map((a) => a.id)));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (tech) loadDates(tech); }, [tech]);
  useEffect(() => { loadAssignments(); }, [tech, selectedDate]);

  const doPreview = async () => {
    if (!uploadFile) return toast.error(t("auto.seleziona_un_file_excel"));
    setPreviewing(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    try {
      const { data } = await api.post("/assignments/preview-excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewData(data);
      setColMapping({ ...(data.default_mapping || {}) });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile leggere il file");
    } finally {
      setPreviewing(false);
    }
  };

  const doUpload = async () => {
    if (!uploadFile) return toast.error(t("auto.seleziona_un_file_excel"));
    setUploading(true);
    setUploadResult(null);
    const fd = new FormData();
    fd.append("file", uploadFile);
    // Se abbiamo un mapping personalizzato dal preview, lo inviamo
    if (previewData && colMapping) {
      fd.append("column_mapping", JSON.stringify(colMapping));
    }
    try {
      const { data } = await api.post(
        `/assignments/upload-excel`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploadResult(data);
      const parts = [
        `${data.added} appuntamenti caricati`,
        `${data.unmatched} da assegnare`,
        `${data.skipped_red || 0} righe rosse escluse`,
      ];
      if (data.marked_ko) parts.push(`${data.marked_ko} impianti marcati come KO (conteggiati nelle performance)`);
      parts.push(`${data.skipped} righe scartate (già chiuse o KO)`);
      if (data.deleted_from_same_file) parts.push(`${data.deleted_from_same_file} pending sostituiti dallo stesso file`);
      toast.success(parts.join(" · "));
      await Promise.all([loadTechs(), loadDates(tech), loadUnmatchedCount()]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const closeUpload = () => {
    setOpenUpload(false);
    setUploadFile(null);
    setUploadResult(null);
    setPreviewData(null);
    setColMapping({});
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Eliminare questa assegnazione?")) return;
    try {
      await api.delete(`/assignments/${id}`);
      toast.success("Eliminata");
      loadAssignments();
      loadDates(tech);
      loadUnmatchedCount();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Attachments per singola assegnazione (PDF, immagini, DOC, ecc.)
  const [uploadingAttId, setUploadingAttId] = useState(null);

  const uploadAttachment = (aid) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.onchange = async () => {
      const f = inp.files?.[0];
      if (!f) return;
      if (f.size > 20 * 1024 * 1024) { toast.error("File troppo grande (max 20 MB)"); return; }
      setUploadingAttId(aid);
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/assignments/${aid}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAssignments((prev) => prev.map((x) => (x.id === aid ? { ...x, attachments: [...(x.attachments || []), data] } : x)));
        toast.success("File allegato");
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setUploadingAttId(null); }
    };
    inp.click();
  };

  const deleteAttachment = async (aid, attId) => {
    if (!window.confirm("Rimuovere questo allegato?")) return;
    try {
      await api.delete(`/assignments/${aid}/attachments/${attId}`);
      setAssignments((prev) => prev.map((x) => (x.id === aid ? { ...x, attachments: (x.attachments || []).filter((a) => a.id !== attId) } : x)));
      toast.success("Allegato rimosso");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadAttachment = async (aid, att) => {
    try {
      const res = await api.get(`/assignments/${aid}/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openMap = async () => {
    if (!tech || tech === UNMATCHED || !selectedDate) return;
    try {
      const { data } = await api.get("/route/preview", { params: { technician_id: tech, date: selectedDate } });
      if (data.google_maps_url) {
        window.open(data.google_maps_url, "_blank", "noopener");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore percorso");
    }
  };

  const runOptimize = async () => {
    if (!tech || tech === UNMATCHED || !selectedDate) return;
    setOptimizing(true);
    setOptimizeResult(null);
    try {
      const { data } = await api.post("/route/optimize", null, { params: { technician_id: tech, date: selectedDate } });
      setOptimizeResult(data);
      setOpenOptimize(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ottimizzazione fallita");
    } finally {
      setOptimizing(false);
    }
  };

  const applyRoute = async () => {
    if (!optimizeResult) return;
    setApplyingRoute(true);
    try {
      const ordered_ids = (optimizeResult.optimized?.stops || [])
        .filter((s) => s.type === "stop")
        .map((s) => s.id);
      const { data } = await api.post("/route/apply", {
        technician_id: optimizeResult.technician_id,
        date: optimizeResult.date,
        ordered_assignment_ids: ordered_ids,
      });
      toast.success(`Percorso applicato: ${data.updated} appuntamenti riordinati`);
      setOpenOptimize(false);
      setOptimizeResult(null);
      loadAssignments();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore applicazione");
    } finally {
      setApplyingRoute(false);
    }
  };


  const runDedupe = async () => {
    try {
      const { data: dry } = await api.post("/assignments/dedupe?dry_run=true");
      if (!dry.candidates_to_delete) {
        toast.success("Nessun duplicato trovato: DB già pulito.");
        return;
      }
      const ok = window.confirm(
        `Trovati ${dry.duplicate_groups_found} gruppi di duplicati (${dry.candidates_to_delete} righe da cancellare).\n\n` +
        `Manterrò 1 riga per ogni combinazione (intervention_ref + data + tecnico) con priorità: done > ko > pending.\n\n` +
        `Procedere con la pulizia?`
      );
      if (!ok) return;
      const { data } = await api.post("/assignments/dedupe?dry_run=false");
      toast.success(`Pulizia completata: ${data.actually_deleted} righe duplicate rimosse (${data.duplicate_groups_found} gruppi).`);
      await loadDates(tech);
      loadUnmatchedCount();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore dedupe");
    }
  };


  const clearDay = async () => {
    if (!selectedDate) return;
    if (!window.confirm(`Eliminare TUTTE le assegnazioni del ${formatDay(selectedDate)}?`)) return;
    try {
      const isUnm = tech === UNMATCHED;
      const { data } = await api.post("/assignments/delete-bulk", {
        technician_id: isUnm ? null : tech,
        unmatched: isUnm,
        date: selectedDate,
      });
      toast.success(`${data.deleted} assegnazioni eliminate`);
      await loadDates(tech);
      loadUnmatchedCount();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearAllForTech = async () => {
    if (!tech) return;
    const isUnm = tech === UNMATCHED;
    const targetLabel = isUnm
      ? '"Da assegnare"'
      : (technicians.find((x) => x.id === tech)?.name || "il tecnico");
    if (!window.confirm(
      `Eliminare TUTTE le assegnazioni di ${targetLabel} (tutte le date, tutti gli stati)?\n\nQuesta azione NON può essere annullata.`
    )) return;
    try {
      const { data } = await api.post("/assignments/delete-bulk", {
        technician_id: isUnm ? null : tech,
        unmatched: isUnm,
      });
      toast.success(`${data.deleted} assegnazioni eliminate`);
      setSelectedDate("");
      setAssignments([]);
      await loadDates(tech);
      loadUnmatchedCount();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reassignRow = async (aid, newTechId) => {
    try {
      await api.patch(`/assignments/${aid}`, { technician_id: newTechId });
      toast.success("Riassegnato");
      await Promise.all([loadAssignments(), loadDates(tech), loadUnmatchedCount()]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAliases = async (techId) => {
    const raw = (aliasDrafts[techId] || "").split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.patch(`/users/${techId}`, { aliases: raw });
      toast.success(`Alias di ${data.name} salvati (${raw.length})`);
      setTechnicians((prev) => prev.map((tech) => (tech.id === techId ? { ...t, aliases: raw } : tech)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEmailDialog = () => {
    const targetTech = technicians.find((x) => x.id === tech);
    const defaultAcc = smtpAccounts.find((a) => a.has_password);
    const initialTo = targetTech?.email ? [targetTech.email] : [];
    const defaultReplyOptions = settings?.email_reply_to_options || [];
    setEmailForm({
      to: initialTo,
      toInput: "",
      reply_to: defaultReplyOptions[0] || me?.email || "",
      from_name: settings?.email_from_name_default || "Operativo",
      extra_message: "",
      smtp_account_id: defaultAcc?.id || "",
      attachments: [],
    });
    setOpenEmail(true);
  };

  const addRecipient = (email) => {
    const clean = (email || "").trim().toLowerCase();
    if (!clean) return;
    // validazione semplice
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      toast.error(t("auto.email_non_valida"));
      return;
    }
    if (emailForm.to.includes(clean)) {
      toast.info(t("auto.email_già_aggiunta"));
      return;
    }
    setEmailForm((f) => ({ ...f, to: [...f.to, clean], toInput: "" }));
  };

  const removeRecipient = (email) => {
    setEmailForm((f) => ({ ...f, to: f.to.filter((x) => x !== email) }));
  };

  const onFilePicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const results = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} supera 10MB`);
        continue;
      }
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve((r.result || "").toString().split(",")[1] || "");
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      results.push({ filename: f.name, mime_type: f.type || "application/pdf", size: f.size, content_base64: b64 });
    }
    setEmailForm((f) => ({ ...f, attachments: [...f.attachments, ...results] }));
  };

  const removeAttachment = (idx) => {
    setEmailForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const doSendEmail = async () => {
    if (!emailForm.to.length) return toast.error(t("auto.aggiungi_almeno_un_destinatario"));
    if (isUnmatchedView) return toast.error(t("auto.seleziona_un_tecnico_assegnato"));
    if (emailForm.attachments.length > 0 && !emailForm.smtp_account_id) {
      return toast.error(t("auto.gli_allegati_sono_supportati_solo_con_un_account_smtp_aziend"));
    }
    if (selectedIds.size === 0) return toast.error("Seleziona almeno un appuntamento da inviare");
    setSendingEmail(true);
    try {
      const { data } = await api.post("/assignments/send-day-email", {
        technician_id: tech,
        date: selectedDate,
        to: emailForm.to,
        reply_to: emailForm.reply_to || undefined,
        from_name: emailForm.from_name || undefined,
        extra_message: emailForm.extra_message || undefined,
        smtp_account_id: emailForm.smtp_account_id || undefined,
        attachments: emailForm.attachments.map((a) => ({ filename: a.filename, mime_type: a.mime_type, content_base64: a.content_base64 })),
        assignment_ids: Array.from(selectedIds),
      });
      const via = data.via === "smtp" ? `da ${data.from_email}` : "via Emergent";
      toast.success(`Email inviata a ${data.to.length} destinatar${data.to.length === 1 ? "io" : "i"} · ${data.count} appuntament${data.count === 1 ? "o" : "i"} ${via}`);
      setOpenEmail(false);
      // Ricarica assegnazioni + settings per aggiornare stato inviato/storico
      loadAssignments();
      api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingEmail(false); }
  };

  const totalDates = dates.length;
  const totalItems = useMemo(() => dates.reduce((a, d) => a + d.count, 0), [dates]);
  const selectedTech = tech === UNMATCHED ? { name: "Da assegnare", id: UNMATCHED } : technicians.find((x) => x.id === tech);
  const isUnmatchedView = tech === UNMATCHED;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Pianificazione</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.assignments")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auto.carica_il_file_excel_il_sistema_legge_la_colonna_h_e_assegna")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={openAliases} onOpenChange={setOpenAliases}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="btn-manage-aliases">
                <UserCog className="mr-2 h-4 w-4" /> {t("auto.alias_tecnici")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("auto.alias_abbreviazioni_tecnici")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                  Aggiungi le abbreviazioni usate nel file Excel (colonna H), separate da virgola. Esempio: se nel file scrivi "JAS" o "HAMED" per <b>Mario Rossi</b>, mettili qui. Il matching considera già automaticamente nome e cognome.
                </div>
                <div className="max-h-[50vh] space-y-3 overflow-y-auto">
                  {technicians.map((tech) => (
                    <div key={tech.id} className="rounded-sm border border-slate-200 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="font-semibold text-slate-900">{tech.name}</div>
                        <div className="text-xs text-slate-500">{(tech.aliases || []).length} alias attivi</div>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={aliasDrafts[tech.id] || ""}
                          onChange={(e) => setAliasDrafts((prev) => ({ ...prev, [tech.id]: e.target.value }))}
                          placeholder="es. JAS, HAMED, JASSKAMMURI"
                          className="rounded-sm font-mono text-xs"
                          data-testid={`alias-input-${tech.id}`}
                        />
                        <Button size="sm" onClick={() => saveAliases(tech.id)} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid={`alias-save-${tech.id}`}>{t("common.save")}</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button
            asChild
            variant="outline"
            className="rounded-sm border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900"
            data-testid="btn-download-template"
          >
            <a href="/Template_assegnazioni_attivita.xlsx" download>
              <Download className="mr-2 h-4 w-4" /> {t("auto.scarica_template")}
            </a>
          </Button>

          {currentUser?.role === "admin" && (
            <Button
              variant="outline"
              onClick={runDedupe}
              className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="btn-dedupe-assignments"
              title="Rimuove i duplicati residui dal DB (una riga per intervention_ref+data+tecnico)"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Pulisci Duplicati
            </Button>
          )}

          <Dialog open={openUpload} onOpenChange={(o) => (o ? setOpenUpload(true) : closeUpload())}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-upload-assignments">
                <Upload className="mr-2 h-4 w-4" /> {t("auto.carica_excel_appuntamenti")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{t("auto.carica_file_excel")}</DialogTitle></DialogHeader>
              {/* STEP 1: selezione file (nessun preview ancora) */}
              {!uploadResult && !previewData && (
                <div className="space-y-3 text-sm">
                  <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                    <div className="mb-1 font-semibold">{t("auto.come_funziona")}</div>
                    <ol className="ml-4 list-decimal space-y-0.5">
                      <li>Seleziona il file Excel e clicca <b>"Avanti: mappa colonne"</b></li>
                      <li>{t("auto.per_ogni_campo_del_sistema_scegli_quale_colonna_del_tuo_exce")}</li>
                      <li>{t("auto.conferma_per_importare_le_assegnazioni")}</li>
                    </ol>
                    <div className="mt-2 opacity-80">Le righe <b>colorate di rosso</b> vengono <b>escluse</b> automaticamente. Il sistema riconosce nome, cognome e alias dei tecnici (configurabili).</div>
                  </div>
                  <div>
                    <Label>File Excel (.xlsx / .xls)</Label>
                    <Input type="file" accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="rounded-sm" data-testid="assignments-upload-file" />
                  </div>
                  <div className="rounded-sm border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
                    <b>Sostituzione per file:</b> caricando ad esempio <code className="rounded bg-blue-100 px-1">fibra_agosto_miriam.xlsx</code> vengono rimpiazzati <b>solo</b> i pending caricati in precedenza da <b>quello stesso nome file</b>. I pending caricati da altri file (es. <code className="rounded bg-blue-100 px-1">fibra_agosto_luca.xlsx</code>) restano intatti. Le righe già <code className="rounded bg-blue-100 px-1">done</code> o <code className="rounded bg-blue-100 px-1">ko</code> restano in memoria (invisibili) per non falsare il KPI.
                  </div>
                </div>
              )}

              {/* STEP 2: preview + mapping colonne */}
              {!uploadResult && previewData && (
                <div className="space-y-3 text-sm">
                  <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                    <div className="font-semibold">✓ File letto correttamente</div>
                    <div className="mt-1">Foglio: <b>{previewData.sheet_name}</b> · {previewData.columns.length} colonne trovate</div>
                  </div>
                  <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-2 text-xs font-semibold text-slate-700">{t("auto.mappa_i_campi_del_sistema_alle_colonne_del_tuo_excel")}</div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {Object.entries(previewData.field_labels).map(([field, label]) => {
                        const isRequired = field === "date" || field === "resource";
                        const current = colMapping[field];
                        return (
                          <div key={field} className="rounded-sm border border-slate-200 bg-white p-2">
                            <div className="mb-1 text-xs font-medium text-slate-700">
                              {label}
                              {isRequired && <span className="ml-1 text-red-600">*</span>}
                            </div>
                            <Select
                              value={current === null || current === undefined ? "__none__" : String(current)}
                              onValueChange={(v) => setColMapping((m) => ({ ...m, [field]: v === "__none__" ? null : parseInt(v, 10) }))}
                            >
                              <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`col-map-${field}`}>
                                <SelectValue placeholder="Scegli colonna" />
                              </SelectTrigger>
                              <SelectContent className="max-h-64">
                                <SelectItem value="__none__">— Non importare —</SelectItem>
                                {previewData.columns.map((c) => (
                                  <SelectItem key={c.index} value={String(c.index)}>
                                    <span className="font-mono font-semibold">{c.letter}</span>
                                    {c.header ? <span className="ml-1 text-slate-600">— {c.header}</span> : null}
                                    {c.samples?.[0] ? <span className="ml-1 text-slate-400">({c.samples[0].slice(0, 25)})</span> : null}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      <span className="text-red-600">*</span> campi consigliati per un import corretto
                    </div>
                  </div>
                  <label className="flex items-start gap-2 rounded-sm border border-blue-200 bg-blue-50 p-2 text-xs">
                    <span className="text-blue-900">
                      <b>Sostituzione per file</b> attiva: vengono rimpiazzati solo i pending dello stesso nome file. Le righe di altri file restano intatte. Done/KO restano in memoria per il KPI.
                    </span>
                  </label>
                </div>
              )}

              {/* STEP 3: risultato */}
              {uploadResult && (
                <div className="space-y-2 text-sm">
                  <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">
                    <b>{uploadResult.added}</b> appuntamenti importati · <b>{uploadResult.skipped_red || 0}</b> righe rosse escluse · <b>{uploadResult.skipped}</b> righe vuote/intestazione scartate
                  </div>
                  {uploadResult.unmatched > 0 && (
                    <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-amber-900">
                      <b>{uploadResult.unmatched} righe non riconosciute</b> — imposta alias e ricarica, oppure assegna manualmente dalla vista "Da assegnare".
                      {uploadResult.unmatched_samples?.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs font-mono">
                          {uploadResult.unmatched_samples.map((s, i) => (
                            <div key={i}>· <b>{s.raw_resource || "(vuoto)"}</b> — {s.date} — IMP #{s.intervention_ref}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                {!uploadResult && !previewData && (
                  <Button onClick={doPreview} disabled={previewing || !uploadFile} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="assignments-upload-next">
                    {previewing ? "Analisi file..." : "Avanti: mappa colonne"}
                  </Button>
                )}
                {!uploadResult && previewData && (
                  <>
                    <Button onClick={() => { setPreviewData(null); setColMapping({}); }} variant="outline" className="rounded-sm" data-testid="assignments-upload-back">
                      Indietro
                    </Button>
                    <Button onClick={doUpload} disabled={uploading} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="assignments-upload-confirm">
                      {uploading ? "Caricamento..." : "Conferma e importa"}
                    </Button>
                  </>
                )}
                {uploadResult && (
                  <Button onClick={closeUpload} className="rounded-sm" data-testid="assignments-upload-close">{t("common.close")}</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Banner da assegnare */}
      {unmatchedCount > 0 && !isUnmatchedView && (
        <button
          onClick={() => setTech(UNMATCHED)}
          className="flex w-full items-center justify-between rounded-sm border border-amber-300 bg-amber-50 p-3 text-left hover:bg-amber-100"
          data-testid="banner-unmatched"
        >
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <AlertCircle className="h-4 w-4" />
            <b>{t("auto.appuntamenti_da_assegnare_hint", { count: unmatchedCount })}</b> — {t("auto.unmatched_column_h_hint")}
          </div>
          <div className="flex items-center gap-1 text-xs font-semibold text-amber-900">
            {t("auto.assegna_ora_link")} <ChevronRight className="h-3.5 w-3.5" />
          </div>
        </button>
      )}

      {/* Selettore tecnico */}
      <div className="rounded-sm border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            <Users className="h-4 w-4" /> {t("auto.vista")}
          </div>
          <Select value={tech} onValueChange={setTech}>
            <SelectTrigger className="w-72 rounded-sm" data-testid="assignments-tech-select"><SelectValue placeholder={t("auto.seleziona_tecnico")} /></SelectTrigger>
            <SelectContent>
              {unmatchedCount > 0 && (
                <SelectItem value={UNMATCHED}>⚠️ {t("auto.da_assegnare_kicker")} ({unmatchedCount})</SelectItem>
              )}
              {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
            <div><b className="text-slate-900">{totalItems}</b> {t("auto.appuntamenti_totali")}</div>
            <div>·</div>
            <div><b className="text-slate-900">{totalDates}</b> {t("auto.giornate_count")}</div>
          </div>
        </div>
      </div>

      {tech && totalDates === 0 && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">
          {t("auto.nessuna_assegnazione_upload_hint", { name: selectedTech?.name || "" })}
        </div>
      )}

      {totalDates > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          {/* Lista giornate */}
          <div className="space-y-1 rounded-sm border border-slate-200 bg-white p-2 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              <Calendar className="h-3.5 w-3.5" /> {t("auto.giornate")}
            </div>
            {dates.map((d) => {
              const isSel = d.date === selectedDate;
              return (
                <button
                  key={d.date}
                  onClick={() => setSelectedDate(d.date)}
                  data-testid={`day-item-${d.date}`}
                  className={`flex w-full items-center justify-between rounded-sm px-3 py-2 text-left text-sm transition-colors ${isSel ? "bg-[#0052FF] text-white" : "hover:bg-slate-100"}`}
                >
                  <div className="flex-1 truncate">
                    <div className="font-semibold">{formatDay(d.date)}</div>
                  </div>
                  <Badge className={`rounded-sm ${isSel ? "bg-white text-[#0052FF]" : "bg-slate-200 text-slate-700"}`}>{d.count}</Badge>
                </button>
              );
            })}
            {["admin", "magazziniere", "assistente"].includes(currentUser?.role) && (
              <div className="mt-2 border-t border-slate-200 pt-2">
                <Button
                  variant="outline"
                  onClick={clearAllForTech}
                  className="w-full rounded-sm border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="btn-clear-all-for-tech"
                  title={isUnmatchedView
                    ? 'Cancella TUTTE le assegnazioni "Da assegnare" (tutte le date)'
                    : "Cancella TUTTE le assegnazioni di questo tecnico (tutte le date)"}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  {isUnmatchedView ? "Svuota Da assegnare" : "Elimina tutte del tecnico"}
                </Button>
              </div>
            )}
          </div>

          {/* Dettaglio giornata */}
          <div className="space-y-3">
            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-sm border p-4 ${isUnmatchedView ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{isUnmatchedView ? t("auto.da_assegnare_kicker") : t("auto.agenda_kicker")}</div>
                <div className="mt-0.5 text-xl font-bold text-slate-900">{formatDay(selectedDate)}</div>
                <div className="text-xs text-slate-500">
                  {assignments.length} {t("auto.appuntamenti_count_label")} · {isUnmatchedView ? t("auto.tecnico_non_riconosciuto") : <>{t("auto.tecnico_prefix")} <b>{selectedTech?.name}</b></>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!isUnmatchedView && assignments.length >= 1 && (
                  <Button
                    variant="outline"
                    onClick={openMap}
                    className="rounded-sm border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF] hover:text-white"
                    data-testid="btn-view-route"
                  >
                    <MapIcon className="mr-1 h-3.5 w-3.5" /> {t("auto.vedi_percorso")}
                  </Button>
                )}
                {!isUnmatchedView && assignments.length >= 2 && (
                  <Button
                    variant="outline"
                    onClick={runOptimize}
                    disabled={optimizing}
                    className="rounded-sm border-purple-400 text-purple-700 hover:bg-purple-50"
                    data-testid="btn-optimize-route"
                  >
                    {optimizing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
                    {optimizing ? "Ottimizzo..." : "Ottimizza con IA"}
                  </Button>
                )}
                {!isUnmatchedView && assignments.length > 0 && (
                  <Button
                    onClick={openEmailDialog}
                    disabled={selectedIds.size === 0}
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="btn-send-email"
                  >
                    <Mail className="mr-1 h-3.5 w-3.5" /> {t("auto.invia_via_email")}
                    {selectedIds.size > 0 && (
                      <span className="ml-1.5 rounded-sm bg-white/25 px-1.5 py-0.5 text-[10px] font-bold">
                        {selectedIds.size}/{assignments.length}
                      </span>
                    )}
                  </Button>
                )}
                <Button variant="outline" onClick={clearDay} className="rounded-sm text-red-600 hover:bg-red-50" data-testid="btn-clear-day">
                  <Trash2 className="mr-1 h-3.5 w-3.5" /> {t("auto.svuota_giornata")}
                </Button>
              </div>
            </div>
            {loading && <div className="rounded-sm border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">{t("auto.caricamento")}</div>}
            {!loading && assignments.length === 0 && <div className="rounded-sm border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">{t("auto.nessun_appuntamento_in_questa_giornata")}</div>}

            {/* Toolbar selezione invio email */}
            {!loading && !isUnmatchedView && assignments.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="h-3.5 w-3.5 text-emerald-600" />
                  <span className="font-semibold text-slate-900">{selectedIds.size}</span> di {assignments.length} selezionat{selectedIds.size === 1 ? "o" : "i"} per email
                  {(() => {
                    const newCount = assignments.filter((a) => !a.email_sent_at).length;
                    const sentCount = assignments.length - newCount;
                    return (
                      <span className="ml-1 text-slate-500">
                        · <span className="text-amber-700 font-semibold">{newCount}</span> nuov{newCount === 1 ? "o" : "i"}
                        {sentCount > 0 && <>, <span className="text-emerald-700 font-semibold">{sentCount}</span> già inviat{sentCount === 1 ? "o" : "i"}</>}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="rounded-sm px-2 py-1 font-semibold text-slate-600 hover:bg-white hover:text-[#0052FF]"
                    onClick={() => setSelectedIds(new Set(assignments.map((a) => a.id)))}
                    data-testid="btn-select-all"
                  >
                    Tutti
                  </button>
                  <button
                    type="button"
                    className="rounded-sm px-2 py-1 font-semibold text-amber-700 hover:bg-white"
                    onClick={() => setSelectedIds(new Set(assignments.filter((a) => !a.email_sent_at).map((a) => a.id)))}
                    data-testid="btn-select-new"
                  >
                    Solo nuovi
                  </button>
                  <button
                    type="button"
                    className="rounded-sm px-2 py-1 font-semibold text-slate-600 hover:bg-white hover:text-red-600"
                    onClick={() => setSelectedIds(new Set())}
                    data-testid="btn-select-none"
                  >
                    Nessuno
                  </button>
                </div>
              </div>
            )}
            {!loading && assignments.map((a) => {
              const isSelected = selectedIds.has(a.id);
              const wasSent = !!a.email_sent_at;
              const toggleSel = () => {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(a.id)) next.delete(a.id);
                  else next.add(a.id);
                  return next;
                });
              };
              return (
              <div key={a.id} className={`rounded-sm border p-4 ${isUnmatchedView ? "border-amber-200 bg-amber-50/40" : isSelected ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={`assignment-${a.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  {!isUnmatchedView && (
                    <label className="mt-1 flex cursor-pointer items-center pt-1" title={wasSent ? `Già inviata il ${new Date(a.email_sent_at).toLocaleString("it-IT")}` : "Nuovo — mai inviata"}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={toggleSel}
                        className="h-4 w-4 cursor-pointer rounded-sm border-slate-300 text-emerald-600 accent-emerald-600"
                        data-testid={`chk-select-${a.id}`}
                      />
                    </label>
                  )}
                  <div className="flex min-w-[90px] flex-col items-center rounded-sm bg-slate-900 px-3 py-2 text-white">
                    <Clock className="h-3.5 w-3.5 opacity-80" />
                    <div className="font-mono text-lg font-bold">{a.time || "—"}</div>
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    {a.previous_ko && a.previous_ko.length > 0 && (
                      <div className="mb-2 flex items-start gap-2 rounded-sm border-l-4 border-amber-500 bg-amber-50 px-3 py-2" data-testid={`prev-ko-alert-${a.id}`}>
                        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
                        <div className="min-w-0 flex-1 text-xs">
                          <div className="font-bold uppercase tracking-wider text-amber-800">
                            ⚠️ Impianto già visto ({a.previous_ko.length} KO precedent{a.previous_ko.length > 1 ? "i" : "e"})
                          </div>
                          <div className="mt-1 space-y-0.5 text-amber-900">
                            {a.previous_ko.slice(0, 3).map((k) => (
                              <div key={k.assignment_id}>
                                <b>{k.technician_name}</b> — {k.date} — <span className="font-semibold text-red-700">{k.reason || "—"}</span>
                                {k.note && <span className="text-amber-800"> · {k.note}</span>}
                              </div>
                            ))}
                            {a.previous_ko.length > 3 && (
                              <div className="italic text-amber-700">…e altri {a.previous_ko.length - 3}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-[#0052FF]" />
                      <span className="font-mono text-sm font-bold uppercase tracking-wider text-slate-900">IMP #{a.intervention_ref || "—"}</span>
                      {a.wr_ref && <span className="font-mono text-xs font-bold text-slate-500">· WR {a.wr_ref}</span>}
                      {a.external_status && <Badge className="rounded-sm bg-slate-800 text-white text-[10px] uppercase">{a.external_status}</Badge>}
                      {a.status === "done" && <Badge className="rounded-sm bg-emerald-600 text-white">FATTO</Badge>}
                      {a.status === "ko" && <Badge className="rounded-sm bg-red-600 text-white" title={a.ko_marked_at ? `Marcato KO il ${new Date(a.ko_marked_at).toLocaleString("it-IT")}` : "Impianto marcato KO dal committente"}>KO</Badge>}
                      {a.raw_resource && (
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px]">H: {a.raw_resource}</Badge>
                      )}
                      {!isUnmatchedView && (
                        wasSent
                          ? <Badge className="rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-semibold" title={new Date(a.email_sent_at).toLocaleString("it-IT")}>✓ Inviato</Badge>
                          : <Badge className="rounded-sm border border-amber-200 bg-amber-50 text-amber-800 text-[10px] font-semibold">NUOVO</Badge>
                      )}
                    </div>
                    {a.material && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-700"><Package className="h-3.5 w-3.5 text-slate-400" /> {a.material}</div>
                    )}
                    {a.customer && (
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><User className="h-3.5 w-3.5 text-slate-400" /> {a.customer}</div>
                    )}
                    {(a.address || a.city) && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-700"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {a.address}{a.city && <span className="text-slate-500">, {a.city}</span>}</div>
                    )}
                    {a.phone && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" /> <a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a></div>
                    )}
                    {a.notes && (
                      <div className="mt-1 flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1 text-xs text-amber-900"><AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" /> {a.notes}</div>
                    )}
                    {/* Reassign inline */}
                    <div className="mt-2 flex items-center gap-2">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{t("auto.assegnato_a")}</Label>
                      <Select value={a.technician_id || "__none__"} onValueChange={(v) => reassignRow(a.id, v === "__none__" ? null : v)}>
                        <SelectTrigger className="w-56 rounded-sm text-xs" data-testid={`reassign-${a.id}`}>
                          <SelectValue placeholder={t("auto.da_assegnare")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Da assegnare —</SelectItem>
                          {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Allegati (PDF/immagini/documenti) — Admin/Assistente */}
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Allegati</Label>
                      {(a.attachments || []).map((att) => (
                        <div key={att.id} className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs" data-testid={`attachment-${a.id}-${att.id}`}>
                          <FileText className="h-3 w-3 text-slate-500" />
                          <button type="button" onClick={() => downloadAttachment(a.id, att)} className="max-w-[180px] truncate text-slate-800 hover:text-[#0052FF] hover:underline" title={att.filename}>
                            {att.filename}
                          </button>
                          <button type="button" onClick={() => deleteAttachment(a.id, att.id)} className="ml-1 text-red-500 hover:text-red-700" title="Rimuovi allegato" data-testid={`attachment-del-${a.id}-${att.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-xs"
                        onClick={() => uploadAttachment(a.id)}
                        disabled={uploadingAttId === a.id}
                        data-testid={`attachment-add-${a.id}`}
                      >
                        {uploadingAttId === a.id ? (
                          <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Caricamento…</>
                        ) : (
                          <><Paperclip className="mr-1 h-3 w-3" /> Allega file</>
                        )}
                      </Button>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteOne(a.id)} data-testid={`assignment-del-${a.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dialog invio email */}
      <Dialog open={openEmail} onOpenChange={setOpenEmail}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-600" /> Invia agenda via email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <div><b>Giornata:</b> {formatDay(selectedDate)}</div>
              <div><b>{t("auto.tecnico")}</b> {selectedTech?.name}</div>
              <div><b>Appuntamenti:</b> {assignments.length}</div>
            </div>
            <div>
              <Label>Invia da (account aziendale)</Label>
              <Select value={emailForm.smtp_account_id || "__emergent__"} onValueChange={(v) => setEmailForm({ ...emailForm, smtp_account_id: v === "__emergent__" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="email-smtp-account">
                  <SelectValue placeholder="Seleziona mittente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__emergent__">📤 Emergent (mittente generico)</SelectItem>
                  {smtpAccounts.filter((a) => a.has_password).map((a) => (
                    <SelectItem key={a.id} value={a.id}>✉️ {a.label} — {a.from_email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {smtpAccounts.length === 0 && (
                <div className="mt-1 text-[11px] text-amber-700">Nessun account aziendale configurato. Vai in <b>Account Email</b> per aggiungerne uno.</div>
              )}
            </div>
            <div>
              <Label>Mittente (nome visualizzato)</Label>
              <Input
                value={emailForm.from_name}
                onChange={(e) => setEmailForm({ ...emailForm, from_name: e.target.value })}
                placeholder="es. Operativo"
                className="rounded-sm"
                data-testid="email-from-name"
              />
            </div>
            <div>
              <Label>Rispondi a</Label>
              <div className="flex gap-2">
                <Select value={emailForm.reply_to || "__custom__"} onValueChange={(v) => setEmailForm({ ...emailForm, reply_to: v === "__custom__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm flex-1" data-testid="email-reply-to-select">
                    <SelectValue placeholder={t("auto.seleziona_o_inserisci")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(settings?.email_reply_to_options || []).map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                    <SelectItem value="__custom__">— Personalizzato —</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {!(settings?.email_reply_to_options || []).includes(emailForm.reply_to) && (
                <Input
                  type="email"
                  value={emailForm.reply_to}
                  onChange={(e) => setEmailForm({ ...emailForm, reply_to: e.target.value })}
                  placeholder="es. ufficio@tuaimpresa.it"
                  className="mt-1 rounded-sm font-mono text-xs"
                  data-testid="email-reply-to"
                />
              )}
            </div>
            <div>
              <Label>Destinatari *</Label>
              {emailForm.to.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1" data-testid="email-recipients-chips">
                  {emailForm.to.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 rounded-sm bg-[#0052FF] px-2 py-0.5 text-xs text-white">
                      {e}
                      <button
                        onClick={() => removeRecipient(e)}
                        className="ml-0.5 hover:bg-white/20 rounded"
                        data-testid={`email-remove-${e}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={emailForm.toInput}
                  onChange={(e) => setEmailForm({ ...emailForm, toInput: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addRecipient(emailForm.toInput);
                    }
                  }}
                  placeholder="Aggiungi email e premi Invio"
                  className="rounded-sm font-mono"
                  data-testid="email-to-input"
                />
                <Button type="button" variant="outline" onClick={() => addRecipient(emailForm.toInput)} className="rounded-sm" data-testid="email-add-recipient">
                  Aggiungi
                </Button>
              </div>
              {(settings?.email_recipients_history?.length || 0) > 0 && (
                <div className="mt-2">
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{t("auto.recenti_click_per_aggiungere")}</div>
                  <div className="flex flex-wrap gap-1">
                    {settings.email_recipients_history.slice(0, 12).filter((e) => !emailForm.to.includes(e)).map((e) => (
                      <span
                        key={e}
                        className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 pl-2 pr-1 py-0.5 text-xs text-slate-700"
                      >
                        <button
                          onClick={() => addRecipient(e)}
                          className="hover:text-[#0052FF]"
                          data-testid={`email-history-${e}`}
                          title="Aggiungi ai destinatari"
                        >
                          + {e}
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Rimuovere ${e} dallo storico?`)) return;
                            try {
                              await api.delete(`/assignments-email-history?email=${encodeURIComponent(e)}`);
                              const r = await api.get("/settings");
                              setSettings(r.data);
                            } catch (err) { toast.error("Errore rimozione"); }
                          }}
                          className="rounded-sm px-1 text-slate-400 hover:bg-red-100 hover:text-red-700"
                          data-testid={`email-history-remove-${e}`}
                          title="Elimina dallo storico"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Allegati PDF (opzionali, max 10MB — richiede SMTP)</Label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                multiple
                onChange={(e) => onFilePicked(e.target.files)}
                className="rounded-sm text-xs"
                disabled={!emailForm.smtp_account_id}
                data-testid="email-attachment-input"
              />
              {emailForm.attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {emailForm.attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                      <span className="truncate font-mono">📎 {att.filename} <span className="text-slate-400">({Math.round(att.size / 1024)} KB)</span></span>
                      <button onClick={() => removeAttachment(idx)} data-testid={`email-att-remove-${idx}`}>
                        <X className="h-3 w-3 text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {!emailForm.smtp_account_id && (
                <div className="mt-1 text-[11px] text-amber-700">{t("auto.emergent_non_consente_allegati_scegli_un_account_smtp_per_al")}</div>
              )}
            </div>
            <div>
              <Label>Messaggio (opzionale)</Label>
              <textarea
                value={emailForm.extra_message}
                onChange={(e) => setEmailForm({ ...emailForm, extra_message: e.target.value })}
                placeholder={t("auto.es_buongiorno_ecco_gli_appuntamenti_di_oggi")}
                rows={3}
                className="w-full rounded-sm border border-slate-200 p-2 text-sm"
                data-testid="email-extra-msg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEmail(false)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button
              onClick={doSendEmail}
              disabled={sendingEmail || !emailForm.to.length}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
              data-testid="email-send-confirm"
            >
              <Send className="mr-1 h-3.5 w-3.5" /> {sendingEmail ? "Invio..." : `Invia a ${emailForm.to.length || "…"} destinatar${emailForm.to.length === 1 ? "io" : "i"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Ottimizza Percorso IA */}
      <Dialog open={openOptimize} onOpenChange={(o) => { if (!o) { setOpenOptimize(false); setOptimizeResult(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" /> Ottimizzazione percorso con IA
            </DialogTitle>
          </DialogHeader>
          {optimizeResult && (
            <div className="space-y-4">
              {/* Summary card */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-center" data-testid="opt-summary-orig">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Percorso attuale</div>
                  <div className="mt-1 font-mono text-2xl font-bold text-slate-900">{optimizeResult.original.total_km} km</div>
                </div>
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="opt-summary-new">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Percorso ottimizzato</div>
                  <div className="mt-1 font-mono text-2xl font-bold text-emerald-800">{optimizeResult.optimized.total_km} km</div>
                </div>
                <div className="rounded-sm border border-purple-200 bg-purple-50 p-3 text-center" data-testid="opt-summary-savings">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-purple-700">Risparmio</div>
                  <div className="mt-1 font-mono text-2xl font-bold text-purple-800">−{optimizeResult.savings.km} km</div>
                  <div className="text-[10px] text-purple-700">~{optimizeResult.savings.minutes} min</div>
                </div>
              </div>

              {optimizeResult.geocoded_missing > 0 && (
                <div className="rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  ⚠️ {optimizeResult.geocoded_missing} indirizz{optimizeResult.geocoded_missing === 1 ? "o" : "i"} non geocodificati (l'ottimizzazione potrebbe essere meno accurata)
                </div>
              )}

              {/* AI notes */}
              {optimizeResult.ai_notes && (
                <div className="rounded-sm border border-slate-200 bg-white p-3">
                  <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-700">
                    <Sparkles className="h-3.5 w-3.5" /> Considerazioni IA
                  </div>
                  <div className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed" data-testid="opt-ai-notes">
                    {optimizeResult.ai_notes}
                  </div>
                </div>
              )}

              {/* Before / After */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-sm border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 bg-slate-50 p-2 text-xs font-bold uppercase tracking-widest text-slate-600">
                    Ordine attuale
                  </div>
                  <ol className="p-2 space-y-1">
                    {optimizeResult.original.stops.map((s, i) => (
                      <li key={`orig-${i}`} className={`flex gap-2 rounded-sm p-2 text-xs ${s.type === "home" ? "bg-blue-50" : ""}`}>
                        <span className="mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-slate-800 font-mono text-[10px] font-bold text-white">{i}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900">
                            {s.type === "home" ? "🏠 Casa tecnico" : `${s.time || "—"} · ${s.customer || "—"}`}
                          </div>
                          <div className="truncate text-slate-500">{s.address}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="rounded-sm border border-emerald-200 bg-white">
                  <div className="border-b border-emerald-200 bg-emerald-50 p-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
                    Ordine proposto ✨
                  </div>
                  <ol className="p-2 space-y-1">
                    {optimizeResult.optimized.stops.map((s, i) => (
                      <li key={`opt-${i}`} className={`flex gap-2 rounded-sm p-2 text-xs ${s.type === "home" ? "bg-blue-50" : "hover:bg-emerald-50"}`}>
                        <span className="mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-emerald-600 font-mono text-[10px] font-bold text-white">{i}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900">
                            {s.type === "home" ? "🏠 Casa tecnico" : `${s.customer || "—"}${!s.geocoded ? " ⚠️" : ""}`}
                          </div>
                          <div className="truncate text-slate-500">{s.address}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                <a
                  href={optimizeResult.google_maps_url_optimized}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#0052FF] hover:underline flex items-center gap-1"
                  data-testid="opt-open-maps"
                >
                  <MapIcon className="h-3.5 w-3.5" /> Apri percorso ottimizzato in Google Maps
                </a>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setOpenOptimize(false); setOptimizeResult(null); }} className="rounded-sm">
                    Annulla
                  </Button>
                  <Button
                    onClick={applyRoute}
                    disabled={applyingRoute}
                    className="rounded-sm bg-purple-600 hover:bg-purple-700"
                    data-testid="btn-apply-route"
                  >
                    {applyingRoute ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
                    Applica riordino
                  </Button>
                </div>
              </div>
              <div className="rounded-sm bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900">
                ⚠️ <b>Attenzione</b>: applicare il riordino cambia gli orari degli appuntamenti in slot sequenziali di 60 minuti a partire dal primo orario del giorno. Se hai orari già concordati con i clienti, valuta di ricontattarli prima.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


    </div>
  );
}
